import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import formatName from '@/utils/format/formatName';
import { cn } from '@/utils/lib/utils';
import { Check } from '@/utils/lucide';
import { CheckSquare } from '@/utils/lucide';
import { Square } from '@/utils/lucide';
import { X } from '@/utils/lucide';
import { ChevronDown } from '@/utils/lucide';
import { ChevronUp } from '@/utils/lucide';
import insertTab from '@/utils/mutateSession/insertTab';
import { z_classes } from '@/utils/z';

import store, { RootState } from '@/redux/store';
import { setFlow, updateFlow } from '@/redux/supportSlice';

import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

export const flows: { [key: string]: any } = {
	custom_table_builder_new_list: {
		name: 'Create a new List',
		description: 'Create a new list to serve as the data source for your custom table.',
		steps: ['add_and_apply_filters', 'select_items', 'add_to_new_list'],
		finish: {
			name: 'Return to Table Builder',
			dependencies: ['add_and_apply_filters', 'select_items', 'add_to_new_list'],
			action: () =>
			{
				const sessionData = store.getState().session.sessionData!;

				// Previously was just on CTB, now can support smart_table as well.
				// const tabId = sessionData.tabs.find(t => t.id === 'custom_table_builder')?.id ?? sessionData.tabs.find(t => t.id === 'smart_table')?.id;

				// We've replaced tab ids with the flow ids, so we need to look for the tab with the first action of action_type 'custom_table_builder' or 'smart_table'
				const tabId = sessionData.tabs.find(
					t => t.actions[0]?.action_type === 'custom_table_builder' || t.actions[0]?.action_type === 'smart_table'
				)?.id;

				if (!tabId) return;

				const tab = sessionData.tabs.find(t => t.id === tabId);

				let existingAction = tab?.actions[0]!;
				let existingData = tab?.actions[0]?.data;

				insertTab({
					id: tabId,
					title: tabId === 'custom_table_builder' ? 'Table Builder' : 'Smart Table',
					actions: [
						{
							...existingAction,
							data: {
								...existingData,
								dataSourceTabSelection: 'existing'
							}
						}
					],
					session_id: sessionData.id,
					created_at: new Date().toISOString()
				});
			}
		}
	}
};

const GuidedFlowTracker = () =>
{
	const flowData = useSelector((state: RootState) => state.support.flow);
	//console.log('flowData', flowData);

	useEffect(() =>
	{
		if (flowData?.trigger)
		{
			if (!flows[flowData?.trigger]) return;

			const steps = flows[flowData?.trigger]?.steps;
			const progress: Record<string, boolean> = {};
			steps.forEach((step: string) =>
			{
				progress[step] = false;
			});

			store.dispatch(
				setFlow({
					name: formatName(flows[flowData?.trigger]?.name ?? flowData?.trigger),
					key: flowData?.trigger,
					steps,
					progress
				})
			);
		}
	}, [flowData?.trigger]);

	const [open, setOpen] = useState(true);

	if (!flowData) return <></>;
	if (flowData.trigger) return <></>;

	return (
		<div
			className={cn(
				'absolute bottom-4 right-4 p-2 flex flex-col gap-2 rounded-md border shadow-sm min-w-[ch]',
				z_classes.overlay[2],
				flowData.completed ? 'bg-maven-primary-200 text-maven-primary-600' : 'bg-white'
			)}
		>
			<Collapsible open={open} onOpenChange={setOpen}>
				<div className='flex w-full justify-between py-2'>
					<CollapsibleTrigger className='flex flex-row items-center w-full justify-between'>
						{flowData.name}
						<span className='text-icon'>{open ? <ChevronDown /> : <ChevronUp />}</span>
					</CollapsibleTrigger>
					<div className='flex flex-row'>
						<button className='text-icon-sm' onClick={() => store.dispatch(setFlow(null))}>
							{flowData.completed ? <Check className='text-maven-primary-600' /> : <X />}
						</button>
					</div>
				</div>
				<CollapsibleContent>
					<div className='flex flex-col gap-2'>
						{flowData?.steps?.map((step: string) =>
						{
							const complete = !!(flowData.progress ? flowData.progress[step] : false);

							return (
								<div
									key={step}
									className={cn(
										'flex flex-row items-center gap-2',
										complete ? 'line-through decoration-maven-accent-300 decoration-2' : 'text-gray-500'
									)}
								>
									<span className='text-icon-sm'>{complete ? <CheckSquare /> : <Square />}</span>
									{formatName(step)}
								</div>
							);
						})}

						{flows[flowData?.key]?.finish && (
							<Button
								variant='primary'
								onClick={() =>
								{
									flows[flowData.key].finish.action();

									store.dispatch(
										updateFlow({
											key: 'completed',
											value: true
										})
									);

									setTimeout(() =>
									{
										store.dispatch(setFlow(null));
									}, 1500);
								}}
								disabled={!flows[flowData.key].finish.dependencies.every((dep: string) => flowData.progress[dep])}
							>
								{flows[flowData.key].finish.name}
							</Button>
						)}
					</div>
				</CollapsibleContent>
			</Collapsible>
		</div>
	);
};

export default GuidedFlowTracker;
